import type { PlanetName } from './astroEngine';

const PLANET_LIST: PlanetName[] = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'];

const SIGN_LORD: Record<number, PlanetName> = {
  1:'Mars',2:'Venus',3:'Mercury',4:'Moon',5:'Sun',6:'Mercury',
  7:'Venus',8:'Mars',9:'Jupiter',10:'Saturn',11:'Saturn',12:'Jupiter',
};

const PARASHARI_OFFSETS: Record<PlanetName, number[]> = {
  Sun:[7],
  Moon:[7],
  Mercury:[7],
  Venus:[7],
  Mars:[4,7,8],
  Jupiter:[5,7,9],
  Saturn:[3,7,10],
  Rahu:[5,7,9],
  Ketu:[5,7,9],
};

export type HouseInfluenceEntry = {
  planet: PlanetName;
  pct: number;
  w: number;                   // positional weight used in totals (always positive: 1.0 or 0.5)
  role?: 'benefic' | 'malefic' | 'neutral';
  displayW?: number;           // signed weight for UI (+1 / -1, +0.5 / -0.5)
};

export type HouseInfluenceRow = {
  house: number;
  lord: PlanetName;
  lordStrength?: number;
  residents: HouseInfluenceEntry[];
  aspectors: HouseInfluenceEntry[];
  total: number;
  activities?: string;
};

export type HouseInfluenceParams = {
  cusps: number[] | null | undefined;
  placements: Partial<Record<PlanetName, { house: number }>> | null | undefined;
  primaryStrength?: Partial<Record<PlanetName, number>> | null | undefined;
  fallbackStrength?: Partial<Record<PlanetName, number>> | null | undefined;
  aspects?: Array<{ from: PlanetName; toHouse?: number | null }>;
  autoParashariAspects?: boolean;
  roundTotals?: boolean;
  ascSign?: number | null;
  functionalMap?: Record<string, 'benefic' | 'malefic' | 'neutral'> | null;
};

const DEFAULT_PARAMS: Required<Pick<HouseInfluenceParams, 'autoParashariAspects' | 'roundTotals'>> = {
  autoParashariAspects: true,
  roundTotals: true,
};

function resolveStrength(
  planet: PlanetName,
  primary?: Partial<Record<PlanetName, number>> | null,
  fallback?: Partial<Record<PlanetName, number>> | null,
): number {
  if (primary && typeof primary[planet] === 'number' && Number.isFinite(primary[planet]!)) {
    return primary[planet]!;
  }
  if (fallback && typeof fallback[planet] === 'number' && Number.isFinite(fallback[planet]!)) {
    // Node policy (Option B): do NOT auto-shift nodes to negative.
    if (planet === 'Rahu' || planet === 'Ketu') return 0;
    // sv percentages are 0..100 with 50 neutral. Convert to signed -50..+50.
    return Math.round(fallback[planet]! - 50);
  }
  return 0;
}

/**
 * Amplify strength by functional role:
 * - Benefic: negative → reduce by 20%, positive → increase by 20%
 * - Malefic: negative → increase by 20%, positive → reduce by 20%
 * - Neutral: no change
 */
function applyFunctionalAmplifier(
  strength: number,
  role: 'benefic' | 'malefic' | 'neutral'
): number {
  if (role === 'neutral') return strength;
  if (strength === 0) return 0;
  
  if (role === 'benefic') {
    // Benefic: amplify positives, dampen negatives
    return strength > 0 ? strength * 1.20 : strength * 0.80;
  } else {
    // Malefic: dampen positives, amplify negatives
    return strength > 0 ? strength * 0.80 : strength * 1.20;
  }
}

function normalizeCusps(cusps: number[] | null | undefined): number[] | null {
  if (!Array.isArray(cusps) || cusps.length < 12) return null;
  return cusps.slice(0, 12).map(v => Number(v));
}

function normalizePlacements(
  placements: Partial<Record<PlanetName, { house: number }>> | null | undefined
): Record<PlanetName, { house: number }> {
  const out: Record<PlanetName, { house: number }> = {} as any;
  if (!placements) return out;
  for (const planet of PLANET_LIST) {
    const house = placements[planet]?.house;
    if (typeof house === 'number' && house >= 1 && house <= 12) {
      out[planet] = { house };
    }
  }
  return out;
}

function computeOccupants(placements: Record<PlanetName, { house: number }>): Record<number, PlanetName[]> {
  const occupants: Record<number, PlanetName[]> = Object.fromEntries(
    Array.from({ length: 12 }, (_, i) => [i + 1, [] as PlanetName[]])
  ) as Record<number, PlanetName[]>;

  for (const planet of PLANET_LIST) {
    const placement = placements[planet];
    if (!placement) continue;
    const house = placement.house;
    if (house >= 1 && house <= 12) {
      occupants[house].push(planet);
    }
  }

  return occupants;
}

function houseSignFromCusps(house: number, cusps: number[]): number {
  const idx = house - 1;
  const cuspLon = cusps[idx] ?? 0;
  const norm = ((cuspLon % 360) + 360) % 360;
  const sign = Math.floor(norm / 30) + 1;
  if (sign >= 1 && sign <= 12) return sign;
  const wrapped = ((sign - 1 + 12 * 10) % 12) + 1;
  return wrapped;
}

export function computeHouseInfluenceRows(params: HouseInfluenceParams): {
  rows: HouseInfluenceRow[];
  totals: Record<number, number>;
} {
  const autoParashariAspects =
    typeof params.autoParashariAspects === 'boolean'
      ? params.autoParashariAspects
      : DEFAULT_PARAMS.autoParashariAspects;
  const roundTotals =
    typeof params.roundTotals === 'boolean'
      ? params.roundTotals
      : DEFAULT_PARAMS.roundTotals;

  const cusps = normalizeCusps(params.cusps);
  if (!cusps) {
    return { rows: [], totals: {} };
  }

  const placements = normalizePlacements(params.placements);
  const occupants = computeOccupants(placements);

  const autoAspectMap = new Map<number, Set<PlanetName>>();
  if (autoParashariAspects) {
    for (const planet of PLANET_LIST) {
      const placement = placements[planet];
      if (!placement) continue;
      const offsets = PARASHARI_OFFSETS[planet] ?? [7];
      offsets.forEach(offset => {
        const toHouse = ((placement.house - 1 + (offset - 1)) % 12) + 1;
        if (!autoAspectMap.has(toHouse)) autoAspectMap.set(toHouse, new Set<PlanetName>());
        autoAspectMap.get(toHouse)!.add(planet);
      });
    }
  }

  const explicitAspectMap = new Map<number, Set<PlanetName>>();
  if (Array.isArray(params.aspects)) {
    params.aspects.forEach(edge => {
      if (!edge) return;
      const to = Number(edge.toHouse);
      if (!Number.isFinite(to)) return;
      const toHouse = ((Math.round(to) - 1 + 12) % 12) + 1;
      const from = edge.from;
      if (!PLANET_LIST.includes(from)) return;
      if (!explicitAspectMap.has(toHouse)) explicitAspectMap.set(toHouse, new Set<PlanetName>());
      explicitAspectMap.get(toHouse)!.add(from);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const ascSign =
    typeof params.ascSign === 'number' && Number.isFinite(params.ascSign)
      ? ((Math.round(params.ascSign) - 1 + 12 * 10) % 12) + 1
      : 1;

  const baseRole = (planet: PlanetName): 'benefic' | 'malefic' | 'neutral' => {
    if (planet === 'Ketu') return 'malefic';
    if (planet === 'Rahu') return 'neutral';
    const role = params.functionalMap?.[planet];
    return role === 'benefic' || role === 'malefic' ? role : 'neutral';
  };

  const sunVenusTogether = Object.values(occupants).some(
    list => list.includes('Sun') && list.includes('Venus')
  );
  const sunSaturnTogether = Object.values(occupants).some(
    list => list.includes('Sun') && list.includes('Saturn')
  );

  const rahuHouse = placements.Rahu?.house ?? null;
  const rahuCoOcc = typeof rahuHouse === 'number' ? occupants[rahuHouse] : [];
  const rahuAspectorsSet = new Set<PlanetName>();
  if (typeof rahuHouse === 'number') {
    autoAspectMap.get(rahuHouse)?.forEach(p => rahuAspectorsSet.add(p));
    explicitAspectMap.get(rahuHouse)?.forEach(p => rahuAspectorsSet.add(p));
  }

  const rahuHasMaleficCompany = rahuCoOcc.some(p => baseRole(p) === 'malefic' && p !== 'Rahu');
  const rahuHasBeneficCompany = rahuCoOcc.some(p => baseRole(p) === 'benefic');
  const rahuHasMaleficAspect = Array.from(rahuAspectorsSet).some(p => baseRole(p) === 'malefic');
  const rahuHasBeneficAspect = Array.from(rahuAspectorsSet).some(p => baseRole(p) === 'benefic');
  const rahuGlobalNegative = sunVenusTogether || sunSaturnTogether;

  let rahuBonusSign: -1 | 0 | 1 = 0;
  if (rahuGlobalNegative || rahuHasMaleficCompany || rahuHasMaleficAspect) {
    rahuBonusSign = -1;
  } else if ((rahuCoOcc.length <= 1 && !rahuHasMaleficAspect) || rahuHasBeneficCompany || rahuHasBeneficAspect) {
    rahuBonusSign = 1;
  }

  const rows: HouseInfluenceRow[] = [];
  const totals: Record<number, number> = {};

  for (let house = 1; house <= 12; house++) {
    const houseOccupants = occupants[house];
    const hasOccupants = houseOccupants.length > 0;

    const aspectPlanets = new Set<PlanetName>();
    autoAspectMap.get(house)?.forEach(p => aspectPlanets.add(p));
    explicitAspectMap.get(house)?.forEach(p => aspectPlanets.add(p));
    const hasAspects = aspectPlanets.size > 0;

    const signNum = houseSignFromCusps(house, cusps);
    const lord = SIGN_LORD[signNum];
    const lordRole = baseRole(lord);
    const lordStrengthRaw = resolveStrength(lord, params.primaryStrength ?? undefined, params.fallbackStrength ?? undefined);
    const lordStrength = applyFunctionalAmplifier(lordStrengthRaw, lordRole);
    const lordWeight = hasOccupants ? 0.5 : (hasAspects ? 0.75 : 1.0);

    const residents: HouseInfluenceEntry[] = houseOccupants.map(planet => {
      const role = baseRole(planet);
      const pctRaw = resolveStrength(planet, params.primaryStrength ?? undefined, params.fallbackStrength ?? undefined);
      const pct = applyFunctionalAmplifier(pctRaw, role);
      const w = 1.0;
      const displayW = (role === 'malefic') ? -w : +w;
      return { planet, pct, w, role, displayW };
    });

    const aspectWeight = hasOccupants ? 0.5 : 1.0;
    const aspectEntries: HouseInfluenceEntry[] = [];
    for (const planet of PLANET_LIST) {
      const autoHas = autoAspectMap.get(house)?.has(planet) ?? false;
      const explicitHas = explicitAspectMap.get(house)?.has(planet) ?? false;
      if (!autoHas && !explicitHas) continue;
      const role = baseRole(planet);
      const pctRaw = resolveStrength(planet, params.primaryStrength ?? undefined, params.fallbackStrength ?? undefined);
      const pct = applyFunctionalAmplifier(pctRaw, role);
      const displayW = (role === 'malefic') ? -aspectWeight : +aspectWeight;
      aspectEntries.push({ planet, pct, w: aspectWeight, role, displayW });
    }

    let totalRaw = lordStrength * lordWeight;

    for (const entry of residents) {
      totalRaw += entry.pct * entry.w;
      if (entry.planet === 'Rahu' && rahuBonusSign !== 0) {
        totalRaw += 5 * rahuBonusSign * entry.w;
      }
    }

    for (const entry of aspectEntries) {
      if (entry.planet === lord) {
        if (baseRole(lord) === 'benefic') {
          totalRaw += entry.pct * entry.w;
          totalRaw += 5 * entry.w;
        }
        continue;
      }
      totalRaw += entry.pct * entry.w;
      if (entry.planet === 'Rahu' && rahuBonusSign !== 0) {
        totalRaw += 5 * rahuBonusSign * entry.w;
      }
    }

    // NOTE: Bhava Bala is displayed separately in its own column but NOT added to house totals
    // (kept separate per user request)

    const total = roundTotals ? Math.round(totalRaw) : totalRaw;

    rows.push({
      house,
      lord,
      lordStrength,
      residents,
      aspectors: aspectEntries,
      total,
    });

    totals[house] = total;
  }

  return { rows, totals };
}
